import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { getVisitorId } from "../../utils/analytics";

const token = localStorage.getItem("userToken") || null;

export const googleLogin = createAsyncThunk(
  "auth/google",
  async (credential, { rejectWithValue }) => {
    try {
      const visitorId = getVisitorId();
      const response = await axiosInstance.post(`/auth/google`, { credential, visitorId });
      localStorage.setItem("userToken", response.data.token);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Google sign in failed");
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentToken = state.auth.token;
      
      if (!currentToken) return rejectWithValue("No token found");

      const response = await axiosInstance.get(`/auth/me`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return response.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch user");
    }
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (profileData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const currentToken = state.auth.token;
      
      const response = await axiosInstance.put(`/auth/update-profile`, profileData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return response.data.user;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update profile");
    }
  }
);

const initialState = {
  user: null,
  token,
  isAuthenticated: !!token,
  loading: false,
  error: null,
  isNewUser: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      localStorage.removeItem("userToken");
      localStorage.removeItem("visitor_id"); // Generate a new one next time
      sessionStorage.removeItem("session_id");
      sessionStorage.removeItem("last_activity");
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isNewUser = action.payload.isNewUser || false;
        if (action.payload.user.visitorId) {
          localStorage.setItem("visitor_id", action.payload.user.visitorId);
        }
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Current User
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        if (action.payload.visitorId) {
          localStorage.setItem("visitor_id", action.payload.visitorId);
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // If the token is invalid/expired, we should log the user out
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem("userToken");
        localStorage.removeItem("visitor_id");
        sessionStorage.removeItem("session_id");
        sessionStorage.removeItem("last_activity");
      })

      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logoutUser, clearError } = authSlice.actions;
export default authSlice.reducer;
